/**
 * NewsModal Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A comprehensive modal component for displaying news articles with support for:
 * - Dynamic article fetching by ID with smart data source prioritization
 * - Rich media content (images, videos, audio, PDFs)
 * - Embedded repository document links with PDF modal integration
 * - Article navigation and sharing functionality
 * - Responsive design with fullscreen support
 * - Production-ready error handling and loading states
 */
import React, { useEffect, useState, useMemo, useCallback, useRef, useContext } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import 'tippy.js/dist/tippy.css';
import './NewsModal.css';
import { cleanJson, addTargetBlankToLinks, removeFirstBoldTag, replaceCss } from '../utils/textUtils';
import apiHelper from '../utils/apiHelper';
import { FILE_NAMES } from '../utils/constants';
import PDFModal from './PDFModal';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Tippy from '@tippyjs/react';
import { AuthContext } from '../components/Auth/AuthContext';

/**
 * NewsModal Component
 * 
 * @param {Object} data - Complete article data object (optional if articleId provided)
 * @param {string} articleId - Article ID to fetch data for (alternative to data)
 * @param {string} articleType - Type of article: 'submitted' or 'news' (affects data source priority)
 * @param {boolean} show - Whether to display the modal
 * @param {function} onClose - Callback when modal is closed
 * @param {boolean} small - Whether to display in small modal mode
 * @param {boolean} showImage - Whether to show article image
 * @param {function} onNext - Callback for next article navigation
 * @param {function} onPrevious - Callback for previous article navigation
 * @param {boolean} hasNext - Whether there is a next article
 * @param {boolean} hasPrevious - Whether there is a previous article
 * @param {Object} nextArticle - Next article data for navigation preview
 * @param {Object} previousArticle - Previous article data for navigation preview
 */
function NewsModal({ 
    data, 
    articleId, 
    articleType,
    show, 
    onClose, 
    small = false, 
    showImage = true, 
    onNext, 
    onPrevious, 
    hasNext = false, 
    hasPrevious = false, 
    nextArticle = null, 
    previousArticle = null 
}) {

    const navigate = useNavigate();
    const { user: [user] } = useContext(AuthContext);

    // State for article data and loading
    const [articleData, setArticleData] = useState(data || {});
    const [isLoadingArticle, setIsLoadingArticle] = useState(false);

    // Fetch full article data if only ID is provided
    useEffect(() => {
        let isMounted = true;
        
        async function fetchFullArticleIfNeeded() {
            // If we have complete data already, use it
            if (data && data.description) {
                setArticleData(data);
                return;
            }
            
            // If we have an articleId, fetch the complete data
            if (articleId) {
                setIsLoadingArticle(true);
                try {
                    // Try to fetch from different news data files
                    // Prioritize the correct data source based on article type
                    let newsDataSources;
                    if (articleType === 'submitted') {
                        // Search submitted articles first, then fallback to other sources
                        newsDataSources = [
                            FILE_NAMES.FEATURED_AND_SUBMITTED_ARTICLES,
                            FILE_NAMES.NEWS,
                            FILE_NAMES.MONTHLY_NEWS,
                            FILE_NAMES.FEATURED_NEWS
                        ];
                    } else {
                        // Search regular news sources first, then fallback to submitted articles
                        newsDataSources = [
                            FILE_NAMES.NEWS,
                            FILE_NAMES.MONTHLY_NEWS,
                            FILE_NAMES.FEATURED_AND_SUBMITTED_ARTICLES,
                            FILE_NAMES.FEATURED_NEWS
                        ];
                    }
                    
                    let foundArticle = null;
                    
                    // Search through all news data sources
                    for (const fileName of newsDataSources) {
                        try {
                            const newsResult = await apiHelper.fetchJSONFileByNameAndContent(fileName);
                            if (newsResult.fileContent && Array.isArray(newsResult.fileContent)) {
                                foundArticle = newsResult.fileContent.find(article => 
                                    String(article.id) === String(articleId) ||
                                    String(article.ID) === String(articleId) ||
                                    String(article.listItemId) === String(articleId)
                                );
                                if (foundArticle) {
                                    break;
                                }
                            }
                        } catch (err) {
                            // Silently continue to next data source if one fails
                        }
                    }
                    
                    if (isMounted) {
                        if (foundArticle) {
                            setArticleData(foundArticle);
                        } else {
                            // Fallback to provided data if available
                            setArticleData(data || {});
                        }
                    }
                } catch (err) {
                    // Error fetching article data, use fallback
                    if (isMounted) {
                        setArticleData(data || {});
                    }
                    console.error('Error fetching article data:', err);
                }
                
                if (isMounted) {
                    setIsLoadingArticle(false);
                }
            } else if (data) {
                // Use provided data
                setArticleData(data);
            } else {
                // No data provided, use empty object
                setArticleData({});
            }
        }
        
        fetchFullArticleIfNeeded();
        
        return () => {
            isMounted = false;
        };
    }, [articleId, articleType, data]);

    // Memoize the data we extract from props
    const { title, description, summary, video, author, firm } = useMemo(() => ({
        title: articleData?.title || '',
        description: articleData?.description || '',
        summary: articleData?.summary && articleData.summary !== articleData?.description ? articleData.summary : null,
        video: articleData?.video || '',
        author: articleData?.author || '',
        firm: articleData?.firm || '',
        issueDate: articleData?.issueDate || ''
    }), [articleData]);


    const [articleImage, setArticleImage] = useState(null);

    let modalHeight = '500px';
    let modalWidth = '75vw';

    if (articleData?.video) {
        modalHeight = '650px';
    }

    if (articleData?.podcast) {
        modalHeight = '650px';
    }

    if (description && description.length > 4500) {
        modalHeight = '700px';
    }

    if(articleData?.summary) {
        modalHeight = '650px';
    }

    if (articleData?.file) {
        modalHeight = '750px';
        modalWidth = '85vw';
    }





    const getRandomImage = () => {
        return `/assets/img/news/weekly/${Math.floor(Math.random() * 30) + 1}.jpg`;
    };

    useEffect(() => {

        if (articleData?.image) {
            // Check if it's already a blob URL
            if (articleData.image.startsWith('blob:')) {
                setArticleImage(articleData.image);
                return;
            }

            const loadImage = async () => {
                if (articleData.image.includes('assets/img/news/weekly/')) {
                    setArticleImage(articleData.image);
                    return;
                }

                if (articleData.image.includes('https://')) {
                    setArticleImage(articleData.image);
                    return;
                }

                const authToken = await apiHelper.getAuthToken();
                const imageUrl = `${process.env.REACT_APP_API_URL}graph/getMediaContent/${articleData.image}`;
                try {
                    const response = await fetch(imageUrl, {
                        headers: { 'Authorization': `Bearer ${authToken}` }
                    });

                    if (response.ok) {
                        const blob = await response.blob();
                        setArticleImage(URL.createObjectURL(blob));
                    } else {
                        setArticleImage(getRandomImage());
                    }
                } catch {
                    setArticleImage(getRandomImage());
                }
            };

            loadImage();
        } else {
            setArticleImage(getRandomImage());
        }
    }, [articleData?.image]);

    // Log when modal opens
    useEffect(() => {
        if (show && title && user?.profile?.email) {
            try {
                apiHelper.logModalOpen(user.profile.email, 'News Article', title);
            } catch (error) {
                // Silent fail - don't disrupt user experience
            }
        }
    }, [show, title, user?.profile?.email]);

    const [isFullScreen, setIsFullScreen] = useState(false);
    const { showImg, fullScreen } = articleData || {};

    const [videoSource, setVideoSource] = useState(null);
    const [podcastSource, setPodcastSource] = useState(null);
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [pdfSource, setPdfSource] = useState(null);
    const [isVideoExpanded, setIsVideoExpanded] = useState(false);
    const [isVideoEmbedded, setIsVideoEmbedded] = useState(false);

    // Add these states at the top with other states
    const [isVideoLoading, setIsVideoLoading] = useState(true);
    const [isAudioLoading, setIsAudioLoading] = useState(true);
    const [isPodcastEmbedded, setIsPodcastEmbedded] = useState(false);

    // Add a ref to track the current video URL
    const currentBlobUrl = useRef(null);
    const loadingRef = useRef(false);

    // Add state for repository document PDF modal
    const [showRepositoryPdfModal, setShowRepositoryPdfModal] = useState(false);
    const [repositoryPdfData, setRepositoryPdfData] = useState(null);
    
    // Image modal state
    const [showImageModal, setShowImageModal] = useState(false);
    
    // Copy URL state
    const [copyUrlText, setCopyUrlText] = useState('Copy URL');
    
    // Modal positioning ref
    const modalRef = useRef(null);
    
    // Reading time calculation
    const calculateReadingTime = (text) => {
        if (!text) return 0;
        const wordsPerMinute = 200;
        const cleanText = text.replace(/<[^>]*>/g, ''); // Remove HTML tags
        const wordCount = cleanText.split(/\s+/).filter(word => word.length > 0).length;
        return Math.max(1, Math.ceil(wordCount / wordsPerMinute));
    };
    
    // Position modal in center of viewport
    useEffect(() => {
        if (show && modalRef.current && !isFullScreen) {
            const modalElement = modalRef.current;
            
            // Use fixed positioning to center in viewport
            modalElement.style.position = 'fixed';
            modalElement.style.top = '50%';
            modalElement.style.left = '50%';
            modalElement.style.transform = 'translate(-50%, -50%)';
            modalElement.style.zIndex = '1500';
        }
    }, [show, isFullScreen]);

    // Listen for browser fullscreen changes (ESC key, F11, etc.)
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isCurrentlyFullscreen = !!(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.msFullscreenElement
            );
            
            if (isFullScreen && !isCurrentlyFullscreen) {
                // User exited fullscreen via ESC or other means
                setIsFullScreen(false);
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('msfullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('msfullscreenchange', handleFullscreenChange);
        };
    }, [isFullScreen]);


    const toggleFullScreen = () => {
        if (!isFullScreen) {
            // Enter fullscreen - use browser's native fullscreen API
            if (modalRef.current) {
                if (modalRef.current.requestFullscreen) {
                    modalRef.current.requestFullscreen();
                } else if (modalRef.current.webkitRequestFullscreen) {
                    modalRef.current.webkitRequestFullscreen();
                } else if (modalRef.current.msRequestFullscreen) {
                    modalRef.current.msRequestFullscreen();
                }
                setIsFullScreen(true);
            }
        } else {
            // Exit fullscreen
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
            setIsFullScreen(false);
        }
    };

    // Handle repository document link clicks
    const handleRepositoryDocumentClick = (event, linkElement) => {

        try {
            event.preventDefault();
            event.stopPropagation();

            const docId = linkElement.getAttribute('data-doc-id');
            const docTitle = linkElement.getAttribute('data-doc-title');
            const docUrl = linkElement.getAttribute('data-doc-url');
            const docFilename = linkElement.getAttribute('data-doc-filename');

            // Try to get the full URL from data-full-url first, fallback to data-doc-url, then href
            const fullUrl = linkElement.getAttribute('data-full-url') ||
                linkElement.getAttribute('data-doc-url') ||
                linkElement.getAttribute('data-original-href') ||
                linkElement.getAttribute('href');


            // Set up the repository PDF data and open modal
            const pdfData = {
                id: docId || 'unknown',
                title: docTitle || 'Unknown Document',
                url: fullUrl || docUrl || 'unknown-url',
                filename: docFilename || 'unknown.pdf'
            };

            try {
                setRepositoryPdfData(pdfData);
                setShowRepositoryPdfModal(true);

            } catch (stateError) {
                // Error setting state, silently fail
            }

        } catch (error) {
            // Error handling repository document click, silently fail
        }
    };

    // Set up repository link processing after content is rendered
    useEffect(() => {
        if (!show) return;

        let globalHandlerRef = null;

        const processRepositoryLinks = () => {
            // Find all links in the article description
            const allLinks = document.querySelectorAll('.article-description a');

            // Find repository links AND news document links
            const repositoryLinks = Array.from(allLinks).filter(link => {
                const href = link.getAttribute('href') || '';
                const dataOriginalHref = link.getAttribute('data-original-href') || '';
                const urlToCheck = href || dataOriginalHref;

                // Check if it's already been processed - if so, it's definitely a document link
                if (link.hasAttribute('data-processed') || 
                    link.classList.contains('repository-document-link') || 
                    link.classList.contains('news-document-link')) {
                    return true;
                }

                // Check for news document links (new format)
                if (urlToCheck.includes('/files/detail/news/') && urlToCheck.includes('.pdf')) {
                    return true;
                }

                // Check URL patterns for repository links - but exclude external URLs (starting with http:// or https://)
                const isExternalUrl = urlToCheck.startsWith('http://') || urlToCheck.startsWith('https://');
                
                if (isExternalUrl) {
                    // Allow news document links even if they're external URLs
                    if (urlToCheck.includes('/files/detail/news/')) {
                        return true;
                    }
                    // But exclude other external URLs from repository link processing
                    return false;
                }
                
                const isRepositoryByUrl = urlToCheck.includes('/assets/') ||
                    urlToCheck.includes('CBI%') ||
                    urlToCheck.includes('CBI ') ||
                    urlToCheck.includes('.pdf') ||
                    urlToCheck.includes('sharepoint.com') ||
                    /\.(pdf|docx?|xlsx?|pptx?)(\?|$|%)/i.test(urlToCheck);

                return isRepositoryByUrl;
            });

            repositoryLinks.forEach((link, index) => {
                // Skip if already processed to avoid double-processing
                if (link.hasAttribute('data-processed')) {    
                    return;
                }

                const href = link.getAttribute('href') || link.getAttribute('data-original-href') || '';
                const isNewsDocumentLink = href.includes('/files/detail/news/') || link.classList.contains('news-document-link');

                // Disable default navigation and preserve original href
                const originalHref = href;
                link.setAttribute('data-original-href', originalHref);
                link.removeAttribute('href');

                // Extract document info from the URL and text
                let filename, title, articleId;
                
                if (isNewsDocumentLink) {
                    // Handle news document links: /files/detail/news/12345/document.pdf
                    filename = link.getAttribute('data-filename') || href.split('/').pop() || '';
                    title = link.getAttribute('data-doc-title') || link.textContent || decodeURIComponent(filename);
                    articleId = link.getAttribute('data-article-id') || href.match(/\/files\/detail\/news\/(\d+)\//)?.[1] || '';
  
                } else {
                    // Handle repository links (existing logic)
                    filename = href.split('/').pop() || '';
                    title = link.textContent || decodeURIComponent(filename);
                }

                const decodedFilename = decodeURIComponent(filename);

                // Add our data attributes for the PDFModal
                link.setAttribute('data-doc-url', originalHref);
                link.setAttribute('data-doc-title', title);
                link.setAttribute('data-doc-filename', decodedFilename);
                link.setAttribute('data-full-url', originalHref);
                
                // Add news-specific attributes if applicable
                if (isNewsDocumentLink && articleId) {
                    link.setAttribute('data-article-id', articleId);
                }

                // Attach custom click handler for PDF modal
                const handleClick = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    handleRepositoryDocumentClick(e, link);
                    return false;
                };

                // Remove any existing click handlers and add our new one
                link.onclick = null; // Clear any inline handlers
                link.removeEventListener('click', handleClick, true);
                link.removeEventListener('click', handleClick, false);
                link.addEventListener('click', handleClick, true);
                link.addEventListener('click', handleClick, false);

                // Override onclick property as additional safeguard
                link.onclick = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRepositoryDocumentClick(e, link);
                    return false;
                };

                // Ensure the link has the right attributes for styling
                if (isNewsDocumentLink) {
                    link.classList.add('news-document-link');
                } else {
                link.classList.add('repository-document-link');
                }

                // Add visual indicator that this is processed
                link.style.position = 'relative';
                link.setAttribute('data-processed', 'true');

            });

            // Only add global handler once and store reference
            if (!globalHandlerRef) {
                const globalHandler = (e) => {
                    // Don't interfere with clicks inside PDFModal
                    let checkTarget = e.target;
                    while (checkTarget && checkTarget !== document.body) {
                        if (checkTarget.classList && (
                            checkTarget.classList.contains('pdf-modal') ||
                            checkTarget.classList.contains('pdf-modal-overlay') ||
                            checkTarget.closest('.pdf-modal')
                        )) {
                            return; // Don't process clicks inside PDFModal
                        }
                        checkTarget = checkTarget.parentElement;
                    }

                    let target = e.target;
                    for (let i = 0; i < 5 && target; i++) {
                        if (target.tagName === 'A') {
                            const href = target.getAttribute('href') || target.getAttribute('data-original-href') || '';
                            
                            // Exclude external URLs from repository link processing
                            const isExternalUrl = href.startsWith('http://') || href.startsWith('https://');
                            if (isExternalUrl) {
                                return; // Let external URLs behave normally
                            }
                            
                            const isRepositoryLink = href.includes('/assets/') ||
                                href.includes('CBI%') ||
                                href.includes('CBI ') ||
                                href.includes('.pdf') ||
                                href.includes('sharepoint.com') ||
                                target.hasAttribute('data-doc-url') ||
                                target.classList.contains('repository-document-link');

                            const isNewsDocumentLink = href.includes('/files/detail/news/') ||
                                target.classList.contains('news-document-link');

                            if (isRepositoryLink || isNewsDocumentLink) {
                                e.preventDefault();
                                e.stopPropagation();
                                e.stopImmediatePropagation();
                                handleRepositoryDocumentClick(e, target);
                                return false;
                            }
                        }
                        target = target.parentElement;
                    }
                };

                globalHandlerRef = globalHandler;
                document.addEventListener('click', globalHandler, true);
            }
        };

        // Process links with multiple attempts to handle dynamic content loading
        setTimeout(processRepositoryLinks, 50);
        setTimeout(processRepositoryLinks, 200);
        setTimeout(processRepositoryLinks, 500);

        // Set up MutationObserver to handle dynamic content changes
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    setTimeout(processRepositoryLinks, 50);
                }
            });
        });

        // Observe changes in the entire modal
        const modalElement = document.querySelector('.news-modal');
        if (modalElement) {
            observer.observe(modalElement, {
                childList: true,
                subtree: true
            });
        }

        return () => {
            observer.disconnect();
            if (globalHandlerRef) {
                document.removeEventListener('click', globalHandlerRef, true);
                globalHandlerRef = null;
            }
        };
    }, [show]); // Remove data dependency to prevent unnecessary re-runs

    const handleVideoClick = useCallback(() => {
        setIsVideoExpanded(!isVideoExpanded);
    }, [isVideoExpanded]);

    const handlePdfModalOpen = () => {
        setShowPdfModal(true);
    };

    const handleOpenNewsPage = () => {
        // Close the modal first
        onClose();
        
        // Use the same robust article type detection as Copy URL
        let articleType = 'weekly'; // Default fallback
        
        // Check for explicit boolean flags first
        const isSubmitted = Boolean(articleData.isSubmittedArticle) || 
                           articleData.isSubmittedArticle === 'true' || 
                           articleData.isSubmittedArticle === 1;
        
        const isWeekly = Boolean(articleData.isWeeklyNews) || 
                        articleData.isWeeklyNews === 'true' || 
                        articleData.isWeeklyNews === 1;
        
        if (isSubmitted) {
            articleType = 'submitted';
        } else if (isWeekly) {
            articleType = 'weekly';
        } else {
            // If boolean flags are unclear/undefined, use fallback indicators
            const hasAuthor = articleData.author || articleData.submittedAuthor || articleData.submittedCompany;
            const hasWeeklyProps = articleData.weeklyCategory || articleData.weeklySection;
            
            if (hasAuthor && !hasWeeklyProps) {
                articleType = 'submitted';
            } else if (hasWeeklyProps) {
                articleType = 'weekly';
            }
            // Otherwise keep 'weekly' as default
        }
        
        // Store title in sessionStorage for WelcomeHeader to use immediately
        sessionStorage.setItem(`news_title_${articleData?.id}`, articleData?.title || '');
        
        // Navigate to the news page with correct article type, passing current data to avoid re-fetching
        navigate(`/news/${articleType}/${articleData?.id}`, {
            state: {
                currentNews: articleData,
                // If we have access to allNewsData (from parent component), pass it too
                // This would need to be passed as a prop if available
                allNewsData: null // Could be enhanced later if parent passes this
            }
        });
    };

    const handleImageClick = useCallback(() => {
        setShowImageModal(true);
    }, []);

    const handlePrintArticle = useCallback(() => {
        // Create a new window for printing with only the article content
        const printWindow = window.open('', '_blank', 'width=800,height=600');
        
        // Get the article content
        const articleContent = document.querySelector('.news-modal-body');
        if (!articleContent) return;
        
        // Clone the content and remove interactive elements
        const contentClone = articleContent.cloneNode(true);
        
        // Remove elements that shouldn't be printed
        const elementsToRemove = contentClone.querySelectorAll(`
            .inline-action-toolbar,
            .member-tools-section,
            .video-close-btn,
            .video-controls,
            .pdf-open-modal-btn,
            .article-video,
            .article-audio,
            .podcast-player,
            .video-container,
            .image-overlay,
            .clickable-image .image-container
        `);
        
        elementsToRemove.forEach(el => el.remove());
        
        // Replace clickable images with regular images
        const clickableImages = contentClone.querySelectorAll('.clickable-image img');
        clickableImages.forEach(img => {
            const newImg = img.cloneNode(true);
            newImg.style.cursor = 'default';
            img.parentNode.replaceWith(newImg);
        });
        
        // Create the HTML for the print window
        const printHTML = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Print: ${title}</title>
                <style>
                    body {
                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                        line-height: 1.6;
                        color: #333;
                        max-width: 800px;
                        margin: 0 auto;
                        padding: 20px;
                    }
                    
                    .news-modal-metadata-section {
                        background: #f8f9fa;
                        border: 1px solid #ddd;
                        padding: 1rem;
                        margin-bottom: 1rem;
                    }
                    
                    .metadata-row-primary, .metadata-row-secondary {
                        margin-bottom: 0.5rem;
                    }
                    
                    .article-type-badge, .category-badge {
                        display: inline-block;
                        padding: 0.25rem 0.5rem;
                        border: 1px solid #333;
                        margin-right: 0.5rem;
                        font-size: 0.8rem;
                        font-weight: bold;
                    }
                    
                    .reading-time, .publish-date, .author-info {
                        font-size: 0.9rem;
                        color: #666;
                        margin: 0.25rem 0;
                    }
                    
                    .article-summary {
                        background: #f9f9f9;
                        padding: 1rem;
                        margin: 1rem 0;
                        border-left: 4px solid #007bff;
                    }
                    
                    .article-description {
                        font-size: 12pt;
                        line-height: 1.5;
                    }
                    
                    .article-image img {
                        max-width: 100%;
                        height: auto;
                        display: block;
                        margin: 1rem 0;
                    }
                    
                    .pdf-iframe-container {
                        border: 1px solid #ddd;
                        padding: 1rem;
                        background: #f9f9f9;
                        text-align: center;
                        font-style: italic;
                    }
                    
                    .pdf-iframe-container::before {
                        content: "PDF Document: Available in digital version";
                        display: block;
                    }
                    
                    a {
                        color: #007bff;
                        text-decoration: underline;
                    }
                    
                    h1, h2, h3, h4, h5, h6 {
                        color: #333;
                        margin-top: 1.5rem;
                        margin-bottom: 0.5rem;
                    }
                    
                    @media print {
                        body { margin: 0; padding: 15px; }
                        .pdf-iframe { display: none; }
                    }
                </style>
            </head>
            <body>
                <h1>${title}</h1>
                ${contentClone.innerHTML}
                <hr style="margin-top: 2rem; border: none; border-top: 1px solid #ddd;">
                <p style="font-size: 0.8rem; color: #666; text-align: center;">
                    Printed from Member Portal
                </p>
            </body>
            </html>
        `;
        
        printWindow.document.write(printHTML);
        printWindow.document.close();
        
        // Wait for content to load, then print and close
        printWindow.onload = () => {
            setTimeout(() => {
                printWindow.print();
                printWindow.close();
            }, 250);
        };
    }, [title]);

    const handleCopyUrl = useCallback(async () => {
        try {
            // Construct the URL for this article with robust type detection
            let articleType = 'weekly'; // Default fallback
            
            // Check for explicit boolean flags first
            const isSubmitted = Boolean(articleData.isSubmittedArticle) || 
                               articleData.isSubmittedArticle === 'true' || 
                               articleData.isSubmittedArticle === 1;
            
            const isWeekly = Boolean(articleData.isWeeklyNews) || 
                            articleData.isWeeklyNews === 'true' || 
                            articleData.isWeeklyNews === 1;
            
            if (isSubmitted) {
                articleType = 'submitted';
            } else if (isWeekly) {
                articleType = 'weekly';
            } else {
                // If boolean flags are unclear/undefined, use fallback indicators
                const hasAuthor = articleData.author || articleData.submittedAuthor || articleData.submittedCompany;
                const hasWeeklyProps = articleData.weeklyCategory || articleData.weeklySection;
                
                if (hasAuthor && !hasWeeklyProps) {
                    articleType = 'submitted';
                } else if (hasWeeklyProps) {
                    articleType = 'weekly';
                }
                // Otherwise keep 'weekly' as default
            }
            
            const articleUrl = `${window.location.origin}/news/${articleType}/${articleData?.id}`;
            
            await navigator.clipboard.writeText(articleUrl);
            setCopyUrlText('Copied!');
            setTimeout(() => setCopyUrlText('Copy URL'), 2000);
        } catch (err) {
            // Fallback for older browsers
            let articleType = 'weekly'; // Default fallback
            
            if (articleData.isSubmittedArticle === true) {
                articleType = 'submitted';
            } else if (articleData.isWeeklyNews === true) {
                articleType = 'weekly';
            } else if (articleData.isSubmittedArticle === false && articleData.isWeeklyNews === false) {
                // If both are false, check other indicators
                if (articleData.author || articleData.submittedAuthor || articleData.submittedCompany) {
                    articleType = 'submitted';
                } else if (articleData.weeklyCategory || articleData.weeklySection) {
                    articleType = 'weekly';
                }
                // Otherwise keep 'weekly' as default
            }
            
            const articleUrl = `${window.location.origin}/news/${articleType}/${articleData?.id}`;
            
            const textArea = document.createElement('textarea');
            textArea.value = articleUrl;
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopyUrlText('Copied!');
                setTimeout(() => setCopyUrlText('Copy URL'), 2000);
            } catch (fallbackErr) {
                setCopyUrlText('Copy failed');
                setTimeout(() => setCopyUrlText('Copy URL'), 2000);
            }
            document.body.removeChild(textArea);
        }
    }, [articleData?.id, articleData?.isSubmittedArticle, articleData?.isWeeklyNews, articleData?.author, articleData?.submittedAuthor, articleData?.submittedCompany, articleData?.weeklyCategory, articleData?.weeklySection]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                if (showImageModal) {
                    setShowImageModal(false);
                } else {
                    onClose();
                }
            }
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [onClose, showImageModal]);

    useEffect(() => {
        const loadVideo = async () => {
            if (loadingRef.current || !video) return;

            loadingRef.current = true;
            setIsVideoLoading(true);
            
            if (video.includes('https://')) {
                // Check if it's a YouTube or Vimeo URL that needs iframe embedding
                const isYouTube = video.includes('youtube.com') || video.includes('youtu.be');
                const isVimeo = video.includes('vimeo.com');

                if (isYouTube || isVimeo) {
                    // For YouTube/Vimeo, we'll set a flag to use iframe instead of video element
                    setVideoSource(getEmbeddableVideoUrl(video));
                    setIsVideoEmbedded(true);
                } else {
                    // For other HTTPS URLs, use them directly
                    setVideoSource(video);
                    setIsVideoEmbedded(false);
                }

                setIsVideoLoading(false);
                loadingRef.current = false;
            } else {
                // For local videos, use streaming endpoint instead of downloading entire file
                const baseUrl = process.env.REACT_APP_API_URL.replace('/api/', '/');
                const streamingUrl = `${baseUrl}media/stream/${video}`;
                      
                // Test if the streaming URL is accessible
                try {
                    const testResponse = await fetch(streamingUrl, { 
                        method: 'HEAD',
                        headers: {
                            'Range': 'bytes=0-1'
                        }
                    });
                    
                    if (testResponse.ok || testResponse.status === 206) {
    
                        setVideoSource(streamingUrl);
                        setIsVideoEmbedded(false);
                    } else {
                        // Fallback to blob download if streaming fails
                        const { fileContent } = await apiHelper.fetchVideoByName(video);
                        const newBlobUrl = URL.createObjectURL(fileContent);
                        currentBlobUrl.current = newBlobUrl;
                        setVideoSource(newBlobUrl);
                        setIsVideoEmbedded(false);
                    }
                } catch (error) {
                    // Fallback to blob download if streaming is not available
                    try {
                        const { fileContent } = await apiHelper.fetchVideoByName(video);
                        const newBlobUrl = URL.createObjectURL(fileContent);
                        currentBlobUrl.current = newBlobUrl;
                        setVideoSource(newBlobUrl);
                        setIsVideoEmbedded(false);
                    } catch (blobError) {
                        // Both streaming and blob download failed
                    }
                }
                
                setIsVideoLoading(false);
                loadingRef.current = false;
            }
        };

        loadVideo();

        return () => {
            if (currentBlobUrl.current) {
                URL.revokeObjectURL(currentBlobUrl.current);
                currentBlobUrl.current = null;
            }
            loadingRef.current = false;
        };
    }, [video]);

    useEffect(() => {
        const loadPodcast = async () => {
            if (articleData?.podcast) {
                if (articleData.podcast.includes('https://')) {
        

                    // Check if it's a platform URL that needs iframe embedding
                    const isSpotify = articleData.podcast.includes('spotify.com');
                    const isSoundCloud = articleData.podcast.includes('soundcloud.com');
                    const isAnchor = articleData.podcast.includes('anchor.fm');
                    const isPodLink = articleData.podcast.includes('pod.link');

                    if (isSpotify || isSoundCloud || isAnchor || isPodLink) {
                        // For podcast platforms, use iframe embedding
                        setPodcastSource(getEmbeddablePodcastUrl(articleData.podcast));
                        setIsPodcastEmbedded(true);


                        // Set a timeout to hide loading spinner if iframe doesn't load
                        setTimeout(() => {

                            setIsAudioLoading(false);
                        }, 5000);
                    } else {
                        // For direct audio file URLs, use audio element
                        setPodcastSource(articleData.podcast);
                        setIsPodcastEmbedded(false);
                    }
                }
                else {
                    try {
                        const authToken = await apiHelper.getAuthToken();
                        const podcastUrl = `${process.env.REACT_APP_API_URL}graph/getMediaContent/${articleData.podcast}`;
                        const headers = {
                            'Authorization': `Bearer ${authToken}`
                        };

                        const response = await fetch(podcastUrl, { headers });
                        if (!response.ok) throw new Error('Failed to fetch podcast');

                        const blob = await response.blob();
                        const objectUrl = URL.createObjectURL(blob);
                        setPodcastSource(objectUrl);
                        setIsPodcastEmbedded(false);

                    } catch (err) {
                        // Failed to load podcast
                        setPodcastSource(null);
                        console.error('Error loading podcast:', err);
                    }
                }
            }
        };

        loadPodcast();

        return () => {
            if (podcastSource && podcastSource.startsWith('blob:')) {
                URL.revokeObjectURL(podcastSource);
            }
        };
    }, [articleData?.podcast]);

    useEffect(() => {
        const loadPdf = async () => {
            if (articleData?.file) {
    
                if (articleData.file.includes('https://')) {
                    // Check if it's actually a PDF file
                    const isPdfFile = articleData.file.toLowerCase().includes('.pdf') ||
                        articleData.file.toLowerCase().includes('pdf') ||
                        articleData.file.includes('application/pdf');

                    if (isPdfFile) {

                        setPdfSource(articleData.file);
                    } else {

                        // For non-PDF URLs, we can't embed them due to CSP restrictions
                        // Set to null to show the fallback message
                        setPdfSource(null);
                    }
                } else {
                    try {

                        let response;
                        try {
                            response = await apiHelper.fetchPDFNameAndContentByTitle(articleData.file);
                        } catch (err) {
                            // Fallback to the direct URL approach
                            const authToken = await apiHelper.getAuthToken();
                            const pdfUrl = `${process.env.REACT_APP_API_URL}graph/getMediaContent/${articleData.file}`;

                            const fetchResponse = await fetch(pdfUrl, {
                                headers: { 'Authorization': `Bearer ${authToken}` }
                            });

                            if (!fetchResponse.ok) throw new Error('Failed to fetch PDF');

                            const blob = await fetchResponse.blob();
                            response = { fileContent: blob };
                        }

                        if (response?.fileContent) {
                            const pdfBlob = new Blob([response.fileContent], { type: 'application/pdf' });
                            const pdfBlobUrl = URL.createObjectURL(pdfBlob);
                            setPdfSource(pdfBlobUrl);
                        } else {
                            throw new Error('No PDF content received');
                        }

                    } catch (err) {
                        // Failed to load PDF
                        setPdfSource(null);
                        console.error('Error loading PDF:', err);
                    }
                }
            }
        };

        loadPdf();

        return () => {
            if (pdfSource && pdfSource.startsWith('blob:')) {
                URL.revokeObjectURL(pdfSource);
            }
        };
    }, [articleData?.file]);

    const hasDescriptionContent = (description, title) => {
        if (!description) return false;

        // Helper function to normalize apostrophes and quotes (same as in textUtils)
        const normalizeText = (text) => {
            return text
                .replace(/[''`´]/g, "'")  // Normalize different apostrophe types
                .replace(/\u2019/g, "'") // Unicode right single quotation mark (8217)
                .replace(/[""]/g, '"')   // Normalize different quote types
                .replace(/\u0027/g, "'") // Unicode apostrophe
                .replace(/&#39;/g, "'")  // HTML entity apostrophe
                .replace(/&apos;/g, "'") // XML apostrophe entity
                .toLowerCase()
                .trim();
        };

        // Create a temporary div to process the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = addTargetBlankToLinks(cleanJson(description));

        // Check if title exists and matches content
        if (title) {
            const titleWords = normalizeText(title.split(" ").slice(0, 3).join(" "));

            // Find all bold elements and check if any contain the title
            const boldElements = tempDiv.querySelectorAll('b, strong, span[style*="font-weight: bold"], span[style*="font-weight:bold"], strong[style], p strong, p[style*="font-weight: bold"]');

            let titleFound = false;
            boldElements.forEach((boldElement) => {
                if (!titleFound) {
                    const boldText = normalizeText(boldElement.textContent || '');
                    const boldWords = boldText.split(" ").slice(0, 3).join(" ");

                    // Check if this bold element contains the title
                    const isMatch = boldText.includes(titleWords) ||
                        titleWords.includes(boldText) ||
                        boldWords === titleWords;

                    if (isMatch) {
                        titleFound = true;

                        // Find the appropriate parent to remove
                        let elementToRemove = boldElement;

                        // If strong is inside a p tag, consider removing the p tag
                        if (boldElement.tagName === 'STRONG' && boldElement.parentElement && boldElement.parentElement.tagName === 'P') {
                            const pElement = boldElement.parentElement;
                            const pContent = pElement.innerHTML.replace(boldElement.outerHTML, '').trim();
                            if (pContent === '' || pContent.match(/^(\s|&nbsp;|<br\s*\/?>)*$/)) {
                                elementToRemove = pElement;
                            }
                        }

                        // If the element to remove is inside a div that only contains this element, remove the div instead
                        if (elementToRemove.parentElement && elementToRemove.parentElement.tagName === 'DIV') {
                            const divElement = elementToRemove.parentElement;
                            const divContent = divElement.innerHTML.replace(elementToRemove.outerHTML, '').trim();
                            if (divContent === '' || divContent.match(/^(\s|&nbsp;|<br\s*\/?>)*$/)) {
                                elementToRemove = divElement;
                            }
                        }

                        elementToRemove.remove();
                    }
                }
            });
        }

        // Clean up empty p tags
        const emptyParagraphs = tempDiv.querySelectorAll('p');
        emptyParagraphs.forEach(p => {
            const content = p.innerHTML.trim();
            if (content === '<br>' || content === '<br/>' || content === '<br />' ||
                content === '&nbsp;' || content === '' || content === ' ' ||
                content === '&nbsp;&nbsp;' || content.match(/^(\s|&nbsp;|<br\s*\/?>)*$/)) {
                p.remove();
            }
        });

        // Clean up empty div tags as well
        const emptyDivs = tempDiv.querySelectorAll('div');
        emptyDivs.forEach(div => {
            const content = div.innerHTML.trim();
            if (content === '<br>' || content === '<br/>' || content === '<br />' ||
                content === '&nbsp;' || content === '' || content === ' ' ||
                content === '&nbsp;&nbsp;' || content.match(/^(\s|&nbsp;|<br\s*\/?>)*$/)) {
                div.remove();
            }
        });

        // Check if there's meaningful content left
        const finalTextContent = (tempDiv.textContent || tempDiv.innerText || '').trim();
        return finalTextContent.length > 0;
    };

    // Helper function to convert video URLs to embeddable format
    const getEmbeddableVideoUrl = (url) => {
        if (url.includes('youtube.com/watch?v=')) {
            const videoId = url.split('v=')[1].split('&')[0];
            return `https://www.youtube.com/embed/${videoId}`;
        } else if (url.includes('youtu.be/')) {
            const videoId = url.split('youtu.be/')[1].split('?')[0];
            return `https://www.youtube.com/embed/${videoId}`;
        } else if (url.includes('vimeo.com/')) {
            const videoId = url.split('vimeo.com/')[1].split('?')[0];
            return `https://player.vimeo.com/video/${videoId}`;
        }
        return url; // Return original URL if not YouTube/Vimeo
    };

    // Helper function to convert podcast URLs to embeddable format
    const getEmbeddablePodcastUrl = (url) => {
        if (url.includes('spotify.com/episode/')) {
            // Convert Spotify episode URL to embed format
            const episodeId = url.split('episode/')[1].split('?')[0];
            return `https://open.spotify.com/embed/episode/${episodeId}`;
        } else if (url.includes('spotify.com/show/')) {
            // Convert Spotify show URL to embed format
            const showId = url.split('show/')[1].split('?')[0];
            return `https://open.spotify.com/embed/show/${showId}`;
        } else if (url.includes('soundcloud.com/')) {
            // For SoundCloud, we can try to use their oEmbed API or use the URL directly
            // SoundCloud embed URLs typically need to be constructed differently
            return `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true`;
        } else if (url.includes('anchor.fm/')) {
            // Anchor.fm embed format
            return url.replace('anchor.fm/', 'anchor.fm/embed/');
        }
        return url; // Return original URL if not a known podcast platform
    };

    if (!show) return null;

    if (showImg === false) showImage = false;

    
    // Show loading spinner while fetching article data
    if (isLoadingArticle) {
        return (
            <>
                {ReactDOM.createPortal(
                    <div className="news-modal fullscreen">
                        <div className="news-modal-content" style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '300px',
                            width: '500px'
                        }}>
                            <div className="modal-header">
                                <h2>Loading Article...</h2>
                                <div>
                                    <span
                                        className="modalclose pointer fas fa-times"
                                        onClick={onClose}
                                    ></span>
                                </div>
                            </div>
                            <div className="loading-container" style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '200px'
                            }}>
                                <LoadingSpinner />
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
            </>
        );
    }
    
    // Render the modal content
    const modalContent = (

        <div ref={modalRef} className={`news-modal ${isFullScreen ? 'fullscreen' : ''} ${small ? 'small-modal' : ''}`}>
            <div className="news-modal-content" style={!isFullScreen ? {
                overflow: 'hidden',
                height: modalHeight,
                width: modalWidth
            } : {}}>
                <div className="modal-header">
                    <h2 id="modalTitle">{title}</h2>
                    <div>
                            <span
                            className={`modalfullscreen fullscreen-btn fas ${isFullScreen ? 'fa-window-restore' : 'fa-expand'}`}
                                onClick={toggleFullScreen}
                            title={isFullScreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                            ></span>
                        <span
                            className="modalclose pointer fas fa-times"
                            onClick={onClose}
                        ></span>
                    </div>
                </div>
                
                <div className="news-modal-body">
                    {/* Metadata Section */}
                    <div className="news-modal-metadata-section">
                        <div className="metadata-row-primary">
                            {/* Article Type Badge */}
                            <div className="metadata-badges">
                                {(() => {
                                    const isSubmitted = Boolean(articleData?.isSubmittedArticle) || 
                                                       articleData?.isSubmittedArticle === 'true' || 
                                                       articleData?.isSubmittedArticle === 1;
                                    
                                    const isWeekly = Boolean(articleData?.isWeeklyNews) || 
                                                    articleData?.isWeeklyNews === 'true' || 
                                                    articleData?.isWeeklyNews === 1;
                                    
                                    let articleTypeBadge = 'Weekly News';
                                    let badgeClass = 'badge-weekly';
                                    
                                    if (isSubmitted) {
                                        articleTypeBadge = 'Submitted Article';
                                        badgeClass = 'badge-submitted';
                                    } else if (isWeekly) {
                                        articleTypeBadge = 'Weekly News';
                                        badgeClass = 'badge-weekly';
                                    } else {
                                        // Fallback detection
                                        const hasAuthor = articleData?.author || articleData?.submittedAuthor || articleData?.submittedCompany;
                                        const hasWeeklyProps = articleData?.weeklyCategory || articleData?.weeklySection;
                                        
                                        if (hasAuthor && !hasWeeklyProps) {
                                            articleTypeBadge = 'Submitted Article';
                                            badgeClass = 'badge-submitted';
                                        }
                                    }
                                    
                                    return (
                                        <span className={`article-type-badge ${badgeClass}`}>
                                            {articleTypeBadge}
                                        </span>
                                    );
                                })()}
                                
                                {/* Category Badge */}
                                {(articleData?.weeklyCategory || articleData?.weeklySection) && (
                                    <span className={`category-badge ${replaceCss(articleData.weeklyCategory || articleData.weeklySection)}`}>
                                        {articleData.weeklyCategory || articleData.weeklySection}
                                    </span>
                                )}
                            </div>
                            
                            {/* Reading Time & Actions */}
                            <div className="metadata-actions-section">
                                <div className="reading-time">
                                    <i className="fas fa-clock"></i>
                                    {calculateReadingTime(description)} min read
                                </div>
                                
                                {/* Inline Action Buttons */}
                                <div className="inline-action-toolbar">
                                    <Tippy content={copyUrlText === 'Copy URL' ? 'Copy article URL' : 'URL copied!'} placement="bottom">
                                        <button
                                            className={`inline-action-btn ${copyUrlText !== 'Copy URL' ? 'success' : ''}`}
                                            onClick={handleCopyUrl}
                                        >
                                            <i className={`fas ${copyUrlText !== 'Copy URL' ? 'fa-check' : 'fa-link'}`}></i>
                                        </button>
                                    </Tippy>
                                    
                                    <Tippy content="Open in dedicated page" placement="bottom">
                                        <button
                                            className="inline-action-btn"
                                            onClick={handleOpenNewsPage}
                                        >
                                            <i className="fas fa-external-link-alt"></i>
                                        </button>
                                    </Tippy>
                                    
                                    {articleData.file && !articleData.file.includes('https://') && (
                                        <Tippy content="Open PDF in window" placement="bottom">
                                            <button
                                                className="inline-action-btn"
                                                onClick={handlePdfModalOpen}
                                            >
                                                <i className="fas fa-file-pdf"></i>
                                            </button>
                                        </Tippy>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        <div className="metadata-row-secondary">
                            {/* Publication Date */}
                            {(() => {
                                if (!articleData?.portalPublishDate) return null;
                                
                                // Parse DD/MM/YYYY format
                                const dateString = articleData.portalPublishDate;
                                const dateParts = dateString.split('/');
                                
                                if (dateParts.length !== 3) return null;
                                
                                const [day, month, year] = dateParts;
                                // Create date with MM/DD/YYYY format (month is 0-indexed)
                                const dateObj = new Date(year, month - 1, day);
                                
                                if (isNaN(dateObj.getTime())) return null; // Invalid date
                                
                                return (
                                    <div className="publish-date">
                                        <i className="fas fa-calendar-alt"></i>
                                        Published: {dateObj.toLocaleDateString('en-GB', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })}
                                    </div>
                                );
                            })()}
                            
                            {/* Author Info - only show for Featured & Submitted Articles */}
                            {(author || firm) && (() => {
                                // Check if this is a Featured & Submitted Article
                                const isSubmitted = Boolean(articleData?.isSubmittedArticle) || 
                                                   articleData?.isSubmittedArticle === 'true' || 
                                                   articleData?.isSubmittedArticle === 1;
                                
                                const isWeekly = Boolean(articleData?.isWeeklyNews) || 
                                                articleData?.isWeeklyNews === 'true' || 
                                                articleData?.isWeeklyNews === 1;
                                
                                let showAuthor = false;
                                
                                if (isSubmitted) {
                                    showAuthor = true;
                                } else if (!isWeekly) {
                                    // If boolean flags are unclear, use fallback indicators
                                    const hasAuthor = articleData?.author || articleData?.submittedAuthor || articleData?.submittedCompany;
                                    const hasWeeklyProps = articleData?.weeklyCategory || articleData?.weeklySection;
                                    
                                    if (hasAuthor && !hasWeeklyProps) {
                                        showAuthor = true;
                                    }
                                }
                                
                                return showAuthor ? (
                                    <div className="author-info">
                                        <i className="fas fa-user"></i>
                                        {author && <span className="author-name">{author}</span>}
                                        {author && firm && <span className="author-divider">|</span>}
                                        {firm && <span className="author-company">{firm}</span>}
                                    </div>
                                ) : null;
                            })()}
                        </div>
                    </div>
                    
                    {/* Summary Section - only show if we have a separate summary */}
                    {summary && (
                        <div className="article-summary-section">
                            <div className="article-summary">
                                <div dangerouslySetInnerHTML={{ __html: cleanJson(addTargetBlankToLinks(summary)) }} />
                            </div>
                        </div>
                    )}

                    <div className="news-article-layout">
                        <div className="article-content-left">
                            {hasDescriptionContent(description, title) && (
                                <div className="article-description description">
                                    {removeFirstBoldTag(addTargetBlankToLinks(cleanJson(description)), title)}
                                </div>
                            )}

                            {articleData.file && (
                                <div className="article-pdf">

                                    {pdfSource ? (
                                        <div className="pdf-iframe-container">
                                            <iframe
                                                src={pdfSource}
                                                title={`PDF: ${title}`}
                                                className="pdf-iframe"
                                            />

                                        </div>
                                    ) : (
                                        <div style={{ padding: '20px', background: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px' }}>
                                            <i className="fas fa-exclamation-triangle"></i>
                                            {articleData.file.includes('https://') ? (
                                                <>
                                                    This document cannot be embedded due to security restrictions.
                                                    <br />
                                                    <a
                                                        href={articleData.file}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ color: '#856404', textDecoration: 'underline', fontWeight: 'bold' }}
                                                    >
                                                        <i className="fas fa-external-link-alt"></i> Open in new tab
                                                    </a>
                                                </>
                                            ) : (
                                                'PDF is loading...'
                                            )}
                                        </div>
                                    )}

                                </div>
                            )}
                        </div>

                        <div className="article-media-right">
                            {showImage && (
                                <div className="article-image clickable-image">
                                    <div className="image-container" onClick={handleImageClick} title="Click to view image in window">
                                        <img src={articleImage} alt={title} />
                                        <div className="image-overlay">
                                            <i className="fas fa-expand"></i>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {articleData.podcast && (
                                <div className="article-audio">
                                    {isAudioLoading && (
                                        <div className="media-loading">
                                            <LoadingSpinner />
                                        </div>
                                    )}
                                    {isPodcastEmbedded ? (
                                        <iframe
                                            src={podcastSource}
                                            title={`Podcast: ${title}`}
                                            className="podcast-player"
                                            frameBorder="0"
                                            allow="encrypted-media"
                                            style={{ width: '100%', height: '152px', display: isAudioLoading ? 'none' : 'block' }}
                                            onLoad={() => {
                                                setIsAudioLoading(false);
                                            }}
                                            onError={() => {
                                                setIsAudioLoading(false);
                                            }}
                                        />
                                    ) : (
                                        <audio
                                            className="audio"
                                            src={podcastSource}
                                            controls
                                            onLoadStart={() => setIsAudioLoading(true)}
                                            onCanPlay={() => setIsAudioLoading(false)}
                                            style={{ display: isAudioLoading ? 'none' : 'block' }}
                                        />
                                    )}
                                </div>
                            )}

                            {video && (
                                <div className={`article-video ${isVideoExpanded ? 'expanded' : 'compact'}`}>
                                    {isVideoExpanded && (
                                        <button 
                                            className="video-close-btn"
                                            onClick={handleVideoClick}
                                            title="Close video"
                                        >
                                            <i className="fas fa-times"></i>
                                        </button>
                                    )}
                                    {isVideoLoading ? (
                                        <LoadingSpinner />
                                    ) : (
                                        <>
                                            <div className="video-container">
                                                {isVideoEmbedded ? (
                                                    <iframe
                                                        src={videoSource}
                                                        title={title}
                                                        className="video-player"
                                                        frameBorder="0"
                                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                        
                                                    />
                                                ) : (
                                                    <video
                                                        src={videoSource}
                                                        controls
                                                        className="video-player"
                                                        onError={(e) => {
                                                            // Video error handling
                                                        }}
                                                    />
                                                )}
                                            </div>
                                            
                                            <div className="video-controls">
                                                <button 
                                                    className="video-expand-btn"
                                                    onClick={handleVideoClick}
                                                    title={isVideoExpanded ? "Compress video" : "Expand video"}
                                                >
                                                    <i className={`fas fa-${isVideoExpanded ? 'compress' : 'expand'}`}></i>
                                                    {isVideoExpanded ? ' Compress' : ' Expand'}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    


                </div>

                {/* Footer with Member Tools and Navigation */}
                <div className="news-modal-footer-nav">
                    <div className="nav-footer-controls">
                        {/* Previous Navigation */}
                        {hasPrevious && (
                            <Tippy
                                content={
                                    previousArticle ? (
                                        <div className="nav-tooltip-content">
                                            <div className="nav-tooltip-title">{previousArticle.title}</div>
                                            {previousArticle.summary && (
                                                <div className="nav-tooltip-summary">
                                                    {previousArticle.summary.substring(0, 200)}
                                                    {previousArticle.summary.length > 200 ? '...' : ''}
                                                </div>
                                            )}
                                            <div className="nav-tooltip-meta">
                                                {previousArticle.author && (
                                                    <span><i className="fas fa-user"></i> {previousArticle.author}</span>
                                                )}
                                                {previousArticle.portalPublishDate && (
                                                    <span><i className="fas fa-calendar"></i> {previousArticle.portalPublishDate}</span>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        'Previous article'
                                    )
                                }
                                placement="top-start"
                                interactive={true}
                                delay={[500, 200]}
                                maxWidth={300}
                                theme="light-border"
                                offset={[20, 10]}
                                boundary="viewport"
                            >
                                <button 
                                    className="nav-footer-btn nav-prev"
                                    onClick={onPrevious}
                                >
                                    <i className="fas fa-chevron-left"></i>
                                    <span className="nav-btn-text">Prev</span>
                                    {previousArticle && (
                                        <span className="nav-btn-title">
                                            {previousArticle.title?.length > 25 
                                                ? previousArticle.title.substring(0, 25) + '...' 
                                                : previousArticle.title}
                                        </span>
                                    )}
                                </button>
                            </Tippy>
                        )}

                        {/* Member Tools in Center */}
                        <div className="footer-member-tools">
                            <button 
                                className="footer-tool-btn"
                                onClick={handleCopyUrl}
                                title="Copy URL to share with other members"
                            >
                                <i className="fas fa-link"></i>
                                <span className="tool-text">{copyUrlText}</span>
                            </button>
                            <button 
                                className="footer-tool-btn"
                                onClick={() => {
                                    const subject = encodeURIComponent(`Member Portal Article: ${title}`);
                                    const body = encodeURIComponent(`Hi,\n\nI thought you might find this article from our member portal interesting:\n\n${title}\n\n${handleCopyUrl.toString().includes('window.location.origin') ? window.location.origin : 'https://your-portal.com'}/news/${(() => {
                                        let articleType = 'weekly';
                                        const isSubmitted = Boolean(articleData.isSubmittedArticle) || 
                                                           articleData.isSubmittedArticle === 'true' || 
                                                           articleData.isSubmittedArticle === 1;
                                        const isWeekly = Boolean(articleData.isWeeklyNews) || 
                                                        articleData.isWeeklyNews === 'true' || 
                                                        articleData.isWeeklyNews === 1;
                                        if (isSubmitted) {
                                            articleType = 'submitted';
                                        } else if (isWeekly) {
                                            articleType = 'weekly';
                                        } else {
                                            const hasAuthor = articleData.author || articleData.submittedAuthor || articleData.submittedCompany;
                                            const hasWeeklyProps = articleData.weeklyCategory || articleData.weeklySection;
                                            if (hasAuthor && !hasWeeklyProps) {
                                                articleType = 'submitted';
                                            } else if (hasWeeklyProps) {
                                                articleType = 'weekly';
                                            }
                                        }
                                        return articleType;
                                    })()}/${articleData?.id}\n\nBest regards`);
                                    window.location.href = `mailto:?subject=${subject}&body=${body}`;
                                }}
                                title="Email to fellow members"
                            >
                                <i className="fas fa-envelope"></i>
                                <span className="tool-text">Email</span>
                            </button>
                            <button 
                                className="footer-tool-btn"
                                onClick={handlePrintArticle}
                                title="Print article"
                            >
                                <i className="fas fa-print"></i>
                                <span className="tool-text">Print</span>
                            </button>
                        </div>
                        
                        {/* Next Navigation */}
                        {hasNext && (
                            <Tippy
                                content={
                                    nextArticle ? (
                                        <div className="nav-tooltip-content">
                                            <div className="nav-tooltip-title">{nextArticle.title}</div>
                                            {nextArticle.summary && (
                                                <div className="nav-tooltip-summary">
                                                    {nextArticle.summary.substring(0, 200)}
                                                    {nextArticle.summary.length > 200 ? '...' : ''}
                                                </div>
                                            )}
                                            <div className="nav-tooltip-meta">
                                                {nextArticle.author && (
                                                    <span><i className="fas fa-user"></i> {nextArticle.author}</span>
                                                )}
                                                {nextArticle.portalPublishDate && (
                                                    <span><i className="fas fa-calendar"></i> {nextArticle.portalPublishDate}</span>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        'Next article'
                                    )
                                }
                                placement="top-end"
                                interactive={true}
                                delay={[500, 200]}
                                maxWidth={300}
                                theme="light-border"
                                offset={[-20, 10]}
                                boundary="viewport"
                            >
                                <button 
                                    className="nav-footer-btn nav-next"
                                    onClick={onNext}
                                >
                                    <span className="nav-btn-text">Next</span>
                                    {nextArticle && (
                                        <span className="nav-btn-title">
                                            {nextArticle.title?.length > 25 
                                                ? nextArticle.title.substring(0, 25) + '...' 
                                                : nextArticle.title}
                                        </span>
                                    )}
                                    <i className="fas fa-chevron-right"></i>
                                </button>
                            </Tippy>
                        )}
                    </div>
                </div>


            </div>
        </div>

    );

    return (
        <>
            {ReactDOM.createPortal(
                <>
                    {modalContent}
                </>,
                document.body  // Always render to body for proper viewport positioning
            )}

            {ReactDOM.createPortal(
                showPdfModal && (
                    <PDFModal
                        key={articleData?.id}
                        externalUrls={articleData?.externalUrls}
                        title={articleData?.title}
                        url={articleData?.file}
                        show={showPdfModal}
                        onClose={() => setShowPdfModal(false)}
                        allowFullscreen={true}
                        allowMove={true}
                        showLinkinToolBar={true}
                    />
                ),
                document.body  // Render directly to body
            )}

            {ReactDOM.createPortal(
                showImageModal && articleImage && (
                    <div className="image-modal-overlay" onClick={() => setShowImageModal(false)}>
                        <div className="image-modal-container" onClick={(e) => e.stopPropagation()}>
                            <div className="image-modal-header">
                                <h3>
                                  <Tippy content={title}>
                                    <span>{title.length > 50 ? `${title.substring(0, 50)}...` : title}</span>
                                  </Tippy>
                                </h3>
                                <button 
                                    className="image-modal-close"
                                    onClick={() => setShowImageModal(false)}
                                    title="Close image"
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            <div className="image-modal-content">
                                <img src={articleImage} alt={title} />
                            </div>
                        </div>
                    </div>
                ),
                document.body
            )}

            {ReactDOM.createPortal(
                showRepositoryPdfModal && repositoryPdfData && (
                    <>
                        <PDFModal
                            key={`repo-${repositoryPdfData.id}`}
                            title={repositoryPdfData.title}
                            url={repositoryPdfData.url}
                            show={showRepositoryPdfModal}
                            onClose={() => {
                                setShowRepositoryPdfModal(false);
                                setRepositoryPdfData(null);
                            }}
                            allowFullscreen={true}
                            allowMove={true}
                            showLinkinToolBar={true}
                            listItemId={repositoryPdfData.id}
                            externalUrls={false}
                        />
                    </>
                ),
                document.body  // Render directly to body
            )}
        </>
    );
}

export default React.memo(NewsModal, (prevProps, nextProps) => {
    // Only re-render if these props change
    return (
        prevProps.show === nextProps.show &&
        prevProps.articleId === nextProps.articleId &&
        prevProps.articleType === nextProps.articleType &&
        prevProps.data?.id === nextProps.data?.id &&
        prevProps.hasNext === nextProps.hasNext &&
        prevProps.hasPrevious === nextProps.hasPrevious
    );
});